/**
 * RuleReflection — Structured interpretation of an NL rule.
 *
 * UX Spec Section 5.2.5 / Story 1.5 AC#2.
 *
 * Shows: rule type, tables, join keys, conditions, column mappings with confidence.
 * States: reflecting (skeleton), displayed, editing, confirmed, failed.
 */

import { useState } from "react";
import { Check, Edit3, Loader2, X } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RuleReflectionState =
  | "reflecting"
  | "displayed"
  | "editing"
  | "confirmed"
  | "failed";

export interface ColumnMapping {
  source: string;
  target: string;
  confidence: number;
}

export interface RuleInterpretation {
  ruleType: string;
  sourceTable: string;
  targetTable?: string;
  joinKeys: { source: string; target: string }[];
  conditions: { column: string; operator: string; value: string }[];
  columnMappings: ColumnMapping[];
}

export interface RuleReflectionProps {
  interpretation: RuleInterpretation | null;
  state?: RuleReflectionState;
  failureMessage?: string;
  rephraseSuggestions?: string[];
  onConfirm?: (interpretation: RuleInterpretation) => void;
  onEdit?: () => void;
  onReject?: () => void;
  onRephrase?: (suggestion: string) => void;
  /** When set the user may not save the rule: Confirm is disabled and this is
   * shown under the actions (e.g. "Requires the rules:create permission"). */
  confirmDisabledReason?: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function confidenceColor(confidence: number): string {
  if (confidence >= 0.95) return "text-[var(--readiness-excellent)]";
  if (confidence >= 0.75) return "text-[var(--readiness-warning)]";
  return "text-[var(--destructive)]";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RuleReflection({
  interpretation,
  state: controlledState,
  failureMessage,
  rephraseSuggestions = [],
  onConfirm,
  onEdit,
  onReject,
  onRephrase,
  confirmDisabledReason = null,
}: RuleReflectionProps) {
  const [state, setState] = useState<RuleReflectionState>(
    controlledState ?? "reflecting",
  );
  const currentState = controlledState ?? state;

  // Failed / Confirmed — both must be checked before the reflecting/skeleton
  // fallback below: the parent nulls `interpretation` once a rule is
  // confirmed (same as a genuine failure leaving it null), so a naive
  // `!interpretation` check would keep matching the skeleton branch forever
  // instead of ever reaching either terminal state.
  if (currentState === "failed") {
    return (
      <div
        role="region"
        aria-label="Rule interpretation"
        className="rounded-xl border border-[var(--warning)] glass-surface p-4"
      >
        <p className="text-body text-[var(--warning)] mb-2">
          {failureMessage ||
            "Could not interpret your rule. Try rephrasing or use the rule builder."}
        </p>
        {rephraseSuggestions.length > 0 && (
          <div className="space-y-1 mt-3">
            <p className="text-caption text-[var(--muted-foreground)]">
              Try rephrasing:
            </p>
            {rephraseSuggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => onRephrase?.(s)}
                className="block text-left text-caption text-[var(--primary)] hover:underline"
              >
                "{s}"
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Confirmed
  if (currentState === "confirmed") {
    return (
      <div
        role="region"
        aria-label="Rule interpretation"
        className="rounded-xl border border-[var(--readiness-excellent)] glass-surface p-4"
      >
        <div className="flex items-center gap-2 text-[var(--readiness-excellent)]">
          <Check className="w-4 h-4" />
          <span className="text-body font-medium">
            Rule confirmed — executing…
          </span>
        </div>
      </div>
    );
  }

  // Reflecting (skeleton) — also the fallback while interpretation hasn't
  // arrived yet for any other non-terminal state.
  if (currentState === "reflecting" || !interpretation) {
    return (
      <div
        role="region"
        aria-label="Rule interpretation"
        className="rounded-xl glass-surface p-4 space-y-3"
      >
        <div className="flex items-center gap-2 text-[var(--muted-foreground)]">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-body">Interpreting your rule…</span>
        </div>
        {/* Skeleton lines */}
        <div className="space-y-2">
          <div className="h-4 w-3/4 rounded bg-[var(--muted-foreground)]/10 animate-pulse" />
          <div className="h-4 w-1/2 rounded bg-[var(--muted-foreground)]/10 animate-pulse" />
          <div className="h-4 w-2/3 rounded bg-[var(--muted-foreground)]/10 animate-pulse" />
        </div>
      </div>
    );
  }

  // Displayed / Editing
  return (
    <div
      role="region"
      aria-label="Rule interpretation"
      className="rounded-xl glass-surface p-4 space-y-4"
    >
      {/* Rule type */}
      <div className="flex items-center justify-between">
        <span className="px-2 py-0.5 rounded-md bg-[var(--primary)]/15 text-[var(--primary)] text-caption font-medium">
          {interpretation.ruleType}
        </span>
      </div>

      {/* Tables */}
      <div className="text-caption text-[var(--muted-foreground)]">
        <span className="text-[var(--foreground)] font-medium">
          {interpretation.sourceTable}
        </span>
        {interpretation.targetTable && (
          <>
            {" "}
            →{" "}
            <span className="text-[var(--foreground)] font-medium">
              {interpretation.targetTable}
            </span>
          </>
        )}
      </div>

      {/* Join keys */}
      {interpretation.joinKeys.length > 0 && (
        <div>
          <p className="text-caption text-[var(--muted-foreground)] mb-1">
            Join keys:
          </p>
          {interpretation.joinKeys.map((jk, i) => (
            <p key={i} className="text-caption text-[var(--foreground)]">
              {jk.source} → {jk.target}
            </p>
          ))}
        </div>
      )}

      {/* Conditions */}
      {interpretation.conditions.length > 0 && (
        <div>
          <p className="text-caption text-[var(--muted-foreground)] mb-1">
            Conditions:
          </p>
          {interpretation.conditions.map((c, i) => (
            <p key={i} className="text-caption text-[var(--foreground)]">
              {c.column} {c.operator} {c.value}
            </p>
          ))}
        </div>
      )}

      {/* Column mappings with confidence */}
      {interpretation.columnMappings.length > 0 && (
        <div>
          <p className="text-caption text-[var(--muted-foreground)] mb-1">
            Column bindings:
          </p>
          {interpretation.columnMappings.map((m, i) => (
            <div key={i} className="flex items-center gap-2 text-caption">
              <span className="text-[var(--foreground)]">
                {m.source} → {m.target}
              </span>
              <span className={`font-medium ${confidenceColor(m.confidence)}`}>
                ({Math.round(m.confidence * 100)}%)
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2 border-t border-[var(--border)]">
        <button
          onClick={() => {
            if (confirmDisabledReason) return; // aria-disabled, not native disabled: stay focusable
            setState("confirmed");
            onConfirm?.(interpretation);
          }}
          aria-disabled={confirmDisabledReason ? true : undefined}
          aria-describedby={
            confirmDisabledReason ? "rule-confirm-blocked" : undefined
          }
          title={confirmDisabledReason ?? undefined}
          className="flex items-center gap-1 px-4 py-2 rounded-lg bg-[var(--primary)] text-[var(--primary-foreground)] text-caption font-medium hover:opacity-90 transition-opacity aria-disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:hover:opacity-50"
        >
          <Check className="w-3 h-3" />
          Confirm
        </button>
        <button
          onClick={() => {
            setState("editing");
            onEdit?.();
          }}
          className="flex items-center gap-1 px-4 py-2 rounded-lg border border-[var(--border)] text-[var(--foreground)] text-caption font-medium hover:bg-white/5 transition-colors"
        >
          <Edit3 className="w-3 h-3" />
          Edit
        </button>
        <button
          onClick={onReject}
          className="flex items-center gap-1 px-3 py-2 rounded-lg text-[var(--muted-foreground)] text-caption hover:text-[var(--destructive)] transition-colors"
        >
          <X className="w-3 h-3" />
          Reject
        </button>
      </div>
      {confirmDisabledReason && (
        <p
          id="rule-confirm-blocked"
          className="text-caption text-[var(--warning)]"
        >
          {confirmDisabledReason}
        </p>
      )}
    </div>
  );
}
